// ---------------------------------------------------------------------------
// "Funding for land and community projects" page (/funding/projects).
// From the funding page brief (23 October 2026). Project-development and
// funding-preparation support only: Bioregional Ventures does not lend money,
// manage an investment fund, offer financial products or guarantee fundraising
// success.
// ---------------------------------------------------------------------------

export const projectFunding = {
  eyebrow: "Funding",
  title: "Preparing projects for funding",
  heroHeading: "Funding that supports land and community",
  heroIntro:
    "Good projects need funding that fits their purpose, operating needs and long-term responsibilities. Bioregional Ventures helps communities and partners develop a clear project case, explore suitable funding pathways and prepare for productive conversations with funders and supporters.",
  primaryCta: "Discuss your funding needs",
  secondaryCta: "Explore how we can help",

  clearProjectHeading: "A clear project before a funding application",
  clearProjectCopy:
    "Funding is one part of a workable project. Land access, community priorities, governance, delivery capacity and ongoing costs all affect what support is needed. We help bring these elements together so that potential funders can understand the project and the people behind it.",

  helpHeading: "How we can help",
  helpBlocks: [
    {
      title: "Project readiness and funding needs",
      text: "Clarify the purpose, intended outcomes, land access, delivery responsibilities and evidence of community support. Develop a staged budget that separates establishment costs from the resources needed to keep the project operating.",
    },
    {
      title: "Funding pathways and partner mapping",
      text: "Explore grants, philanthropy, community contributions, earned income and strategic partnerships. Where repayable funding may be relevant, identify the questions and specialist input needed to assess whether it fits the project.",
    },
    {
      title: "Funding propositions and applications",
      text: "Turn the project into a clear proposal for the right audience. Prepare the project summary, budget, delivery plan and supporting material, with application development included where agreed in the engagement.",
    },
    {
      title: "Community governance and funding relationships",
      text: "Help define who participates, who decides and how money is allocated and overseen. Support conversations about shared priorities, decision rights, reporting and the practical work needed to maintain the partnership.",
    },
    {
      title: "Reporting and continued learning",
      text: "Agree a proportionate way to demonstrate progress, account for funds and learn from delivery. Combine financial records and useful measures with community experience so that reporting supports both accountability and better decisions.",
    },
  ],
  helpClosing:
    "The work is scoped to your project's stage. You may need an initial readiness review, a funding strategy or support developing a specific proposal.",

  matchHeading: "Match the funding to the work",
  matchIntroBefore:
    "We separate land, establishment and ongoing operating needs, then assess which resources could suit each component. ",
  matchIntroLink: "Explore the funding pathways",
  matchIntroAfter:
    " for an overview of grants, philanthropy, community contributions and repayable capital.",
  matchIntroHref: "/funding",

  landAccessLinkLabel: "Explore land access and community ownership",

  outputsHeading: "What an engagement can produce",
  outputsLabel: "Possible scoped outputs — not every deliverable is included in every engagement.",
  outputs: [
    "A project-readiness assessment and prioritised next steps.",
    "A staged project budget, operating assumptions and funding-gap summary.",
    "A funding-source map and partner engagement plan.",
    "A funder briefing or proposal, with application support where agreed.",
    "A community decision framework and proportionate reporting plan.",
  ],

  faqHeading: "Frequently asked questions",
  faqs: [
    {
      q: "Can you help before we have a complete project plan?",
      a: "Yes. An early engagement can clarify the purpose, land access, people involved, costs and the next decisions needed before approaching funders.",
    },
    {
      q: "Do we need to own the land?",
      a: "Ownership is one possible arrangement. The important starting point is whether the project can secure appropriate access and clear responsibilities. Leases, partnerships and community ownership models can be explored alongside the funding plan.",
    },
    {
      q: "Can you help with a grant application?",
      a: "Application preparation can be included in an agreed scope. First we establish whether the opportunity fits the project and whether the required budget, governance and supporting information are ready.",
    },
    {
      q: "Can related projects approach funders together?",
      a: "Where initiatives have a practical connection, a shared proposition can explain their combined value and resource needs. Each project still needs clear responsibilities and a credible delivery plan.",
    },
  ],

  ctaHeading: "Let us understand your funding needs",
  ctaBody:
    "Tell us about your project, where it is based, who is involved and what you need help to move forward.",
};

// Optional "type of support sought" choices for the enquiry form (brief list).
export const supportTypeOptions = [
  "Project readiness",
  "Funding strategy",
  "Grants and proposals",
  "Community governance",
  "Regional partnerships",
  "Other",
];
